import { create } from 'zustand';
import { io, Socket } from 'socket.io-client';
import api from '../utils/api';
import { getWsBaseUrl } from '../utils/env';
import toast from 'react-hot-toast';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant' | 'system';
  timestamp: string;
  sequenceId: number;
  metadata?: any;
  isOptimistic?: boolean;
  replyToMessageId?: string;
  isDeleted?: boolean;
  readReceipts?: { [userId: string]: string };
}

interface ConversationState {
  socket: Socket | null;
  messages: Message[];
  isConnected: boolean;
  isReconnecting: boolean;
  reconnectAttempts: number;
  error: string | null;
  connect: (conversationId: string) => void;
  disconnect: () => void;
  sendMessage: (conversationId: string, content: string, metadata?: any, replyToMessageId?: string) => void;
  deleteMessage: (conversationId: string, messageId: string) => void;
  editMessage: (conversationId: string, messageId: string, content: string) => void;
  uploadAttachment: (conversationId: string, file: File) => void;
  sendVoice: (conversationId: string, blob: Blob, duration: number, replyTo?: string) => void;
}

const MAX_RECONNECT_ATTEMPTS = 5;
const INITIAL_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 30000;

export const useConversationStore = create<ConversationState>((set, get) => ({
  socket: null,
  messages: [],
  isConnected: false,
  isReconnecting: false,
  reconnectAttempts: 0,
  error: null,
  connect: (conversationId) => {
    const socket = io(`${getWsBaseUrl()}/conversations`, {
      reconnection: true,
      reconnectionDelay: INITIAL_RECONNECT_DELAY,
      reconnectionDelayMax: MAX_RECONNECT_DELAY,
      reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
      transports: ['polling'], // Use polling only (WebSocket upgrade fails through Traefik)
      upgrade: false, // Disable automatic upgrade to prevent console errors
    });

    socket.on('connect', () => {
      const { reconnectAttempts } = get();
      set({
        isConnected: true,
        isReconnecting: false,
        reconnectAttempts: 0,
        error: null
      });

      socket.emit('join', conversationId);

      if (reconnectAttempts > 0) {
        toast.success('Reconnected successfully!');
      }
    });

    socket.on('disconnect', (reason) => {
      set({ isConnected: false });

      if (reason === 'io server disconnect') {
        // Server disconnected, attempt to reconnect manually
        toast.error('Connection lost. Attempting to reconnect...');
        socket.connect();
      } else if (reason === 'io client disconnect') {
        // Client intentionally disconnected, don't show error
      } else {
        toast.error('Disconnected from server');
      }
    });

    socket.on('reconnect_attempt', (attemptNumber) => {
      set({
        isReconnecting: true,
        reconnectAttempts: attemptNumber
      });
      toast.loading(`Reconnecting... (${attemptNumber}/${MAX_RECONNECT_ATTEMPTS})`, {
        id: 'reconnect-toast',
      });
    });

    socket.on('reconnect_failed', () => {
      set({
        isReconnecting: false,
        error: 'Failed to reconnect after multiple attempts'
      });
      toast.error('Failed to reconnect. Please refresh the page.', {
        id: 'reconnect-toast',
        duration: Infinity,
      });
    });

    socket.on('reconnect', (attemptNumber) => {
      set({
        isConnected: true,
        isReconnecting: false,
        reconnectAttempts: 0
      });
      toast.success(`Reconnected after ${attemptNumber} attempts`, {
        id: 'reconnect-toast',
      });
    });

    socket.on('connect_error', (err) => {
      set({ error: err.message });
      console.error('WebSocket connection error:', err);
    });

    socket.on('newMessage', (message: Message) => {
      set((state) => ({
        messages: [...state.messages.filter(m => m.id !== message.id && !m.isOptimistic), message].sort((a, b) => a.sequenceId - b.sequenceId),
      }));
    });

    socket.on('messageDeleted', ({ messageId }: { messageId: string }) => {
      set(state => ({
        messages: state.messages.map(m => 
          m.id === messageId ? { ...m, isDeleted: true, content: 'This message was deleted.' } : m
        ),
      }));
    });

    socket.on('messageEdited', (editedMessage: Message) => {
      set(state => ({
        messages: state.messages.map(m =>
          m.id === editedMessage.id ? { ...m, ...editedMessage } : m
        ),
      }));
    });

    socket.on('readReceiptsUpdated', (updatedMessage: Message) => {
      set(state => ({
        messages: state.messages.map(m =>
          m.id === updatedMessage.id ? { ...m, readReceipts: updatedMessage.readReceipts } : m
        ),
      }));
    });

    set({ socket });

    // Fetch initial messages
    api.get(`/conversations/${conversationId}`)
      .then(response => {
        set({ messages: response.data.messages.sort((a: Message, b: Message) => a.sequenceId - b.sequenceId) });
      })
      .catch(err => {
        set({ error: 'Failed to fetch initial messages' });
      });
  },
  disconnect: () => {
    get().socket?.disconnect();
    set({ socket: null, isConnected: false, messages: [] });
  },
  deleteMessage: async (conversationId: string, messageId: string) => {
    try {
      await api.delete(`/conversations/${conversationId}/messages/${messageId}`);
    } catch (error) {
      toast.error('Failed to delete message');
    }
  },
  editMessage: async (conversationId: string, messageId: string, content: string) => {
    try {
      await api.put(`/conversations/${conversationId}/messages/${messageId}`, { content });
    } catch (error) {
      toast.error('Failed to edit message');
    }
  },
  sendMessage: async (conversationId, content, metadata, replyToMessageId) => {
    const optimisticMessage: Message = {
      id: `optimistic-${Date.now()}`,
      content,
      role: 'user',
      timestamp: new Date().toISOString(),
      sequenceId: (get().messages[get().messages.length - 1]?.sequenceId || 0) + 1,
      isOptimistic: true,
      metadata,
      replyToMessageId,
    };

    set(state => ({
      messages: [...state.messages, optimisticMessage],
    }));

    try {
      await api.post(`/conversations/${conversationId}/messages`, { content, role: 'user', metadata, replyToMessageId });
    } catch (error) {
      set(state => ({
        error: 'Failed to send message',
        messages: state.messages.filter(m => m.id !== optimisticMessage.id),
      }));
    }
  },
  uploadAttachment: async (conversationId: string, file: File) => {
    try {
      const { data: presign } = await api.post('/files/upload-url', {
        fileName: file.name,
        fileType: file.type,
      });
      await api.put(presign.url, file, { headers: { 'Content-Type': file.type } });
      // create message with attachment metadata
      await api.post(`/conversations/${conversationId}/messages`, {
        role: 'USER',
        content: '',
        metadata: {
          attachment: {
            url: presign.key, // backend will sign when broadcasting
            mimeType: file.type,
            name: file.name,
            size: file.size,
          },
        },
      });
    } catch (e) {
      toast.error('File upload failed');
    }
  },
  sendVoice: async (conversationId: string, blob: Blob, duration: number, replyTo?: string) => {
    try {
      const form = new FormData();
      form.append('audio', blob, 'voice.ogg');
      form.append('conversationId', conversationId);
      form.append('duration', String(duration));
      if (replyTo) form.append('replyToMessageId', replyTo);
      await api.post('/voice', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    } catch (e) {
      toast.error('Voice message failed');
    }
  },
}));
